import { Avatar, Group, rem, Text, useComputedColorScheme } from "@mantine/core";
import { Dropzone, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import { IconPhoto, IconUpload, IconX } from "@tabler/icons-react";
import { useCallback, useState } from "react";
import { useTRPC } from "../../../../trpc";
import { FormattedMessage } from "react-intl";
import { useQueryClient } from "@tanstack/react-query";

interface ChangeLogoDropzoneProps {
  accountName: string;
  logo: string | null;
}

export const ChangeLogoDropzone = ({ accountName, logo }: ChangeLogoDropzoneProps) => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const [isUploading, setIsUploading] = useState(false);

  const computedColorScheme = useComputedColorScheme("light", { getInitialValueInEffect: true });

  const bg = computedColorScheme === "dark"
    ? "dark.6"
    : "gray.1";

  const handleLogoDrop = useCallback(([file]: File[]) => {
    if (!file) {
      return;
    }
    const form = new FormData();
    form.append("file", file);

    setIsUploading(true);
    fetch(`/api/accounts/${accountName}/logo`, {
      method: "PUT",
      body: form,
    })
      .then((response: Response) => {
        if (!response.ok) {
          logger.error({
            status: response.status,
            statusText: response.statusText,
          }, "Failed to upload account logo");
          setIsUploading(false);
        }
        else {
          void queryClient.invalidateQueries(trpc.getCurrentAccount.queryFilter()).then(() => {
            setIsUploading(false);
          });
        }
      })
      .catch((error: Error) => {
        setIsUploading(false);
        logger.error({
          error,
        }, "Failed to upload account logo");
      });
  }, [accountName, queryClient]);

  return (
    <Dropzone
      loading={isUploading}
      onDrop={handleLogoDrop}
      maxSize={5 * 1024 ** 2}
      accept={IMAGE_MIME_TYPE}
      multiple={false}
      m="-xl"
      bg={bg}
      style={{ border: "none" }}
    >
      <Group justify="center" gap="xl" mih={220} style={{ pointerEvents: "none" }}>
        <Dropzone.Accept>
          <IconUpload
            style={{ width: rem(52), height: rem(52), color: "var(--mantine-color-blue-6)" }}
            stroke={1.5}
          />
        </Dropzone.Accept>
        <Dropzone.Reject>
          <IconX
            style={{ width: rem(52), height: rem(52), color: "var(--mantine-color-red-6)" }}
            stroke={1.5}
          />
        </Dropzone.Reject>
        <Dropzone.Idle>
          {logo
            ? (
                <Avatar
                  src={`/api/accounts/${accountName}/logo/${logo}`}
                  size="4rem"
                  radius="sm"
                />
              )
            : (
                <IconPhoto
                  style={{ width: rem(52), height: rem(52), color: "var(--mantine-color-dimmed)" }}
                  stroke={1.5}
                />
              )}
        </Dropzone.Idle>

        <div>
          <Text size="xl" inline>
            <FormattedMessage defaultMessage="Drag images here or click" id="text.dragOrClick" />
          </Text>
          <Text size="sm" c="dimmed" inline mt={7}>
            <FormattedMessage defaultMessage="Attached file should not exceed 5mb" id="text.attachFiles" />
          </Text>
        </div>
      </Group>
    </Dropzone>
  );
};
